import { useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";

/**
 * Capture the marketing-segment query param handed off from the landing
 * page (e.g. `?segment=3`).
 *
 * The landing page (sessions.blue) tags outbound app links with
 * `?segment=N` so we can attribute traffic to a specific segmented page
 * (e.g. /for/3 = Topical Podcasts). On first load we:
 *
 *   1. Read `?segment=` from the URL.
 *   2. Persist it to localStorage (first-touch wins — we don't overwrite
 *      an existing value, since first-touch attribution is generally more
 *      stable for marketing reporting; revisit if last-touch is preferred).
 *   3. Strip `?segment=` from the URL via history.replaceState so it
 *      doesn't pollute every subsequent in-app URL or get shared.
 *
 * The persisted shape is `{ id: string, capturedAt: number }` (ms epoch),
 * stored under the key `bds_segment`.
 *
 * Why not a cookie? localStorage is sufficient for client-only
 * personalization and avoids sending data on every API request.
 * Why not a context? Personalization callers can read localStorage
 * directly; a context can be layered on later if reactivity is needed.
 */
const STORAGE_KEY = "bds_segment";

export default function useSegmentCapture() {
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(location.search);
    const raw = params.get("segment");
    if (!raw) return;

    // Validate: numeric, 1-2 digits. Anything else is ignored.
    if (!/^\d{1,2}$/.test(raw)) {
      return;
    }

    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (!existing) {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ id: raw, capturedAt: Date.now() })
        );
      }
    } catch {
      // Safari private mode etc. — non-fatal.
    }

    // Strip ?segment= from the URL so it doesn't ride along forever.
    params.delete("segment");
    const newSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : "",
      hash: location.hash
    });
    // Only run on initial mount / when search changes; the replace will
    // re-trigger this effect once but the second pass returns early
    // because `?segment=` is gone.
  }, [location.search, location.pathname, location.hash, history]);
}

/**
 * Read the captured segment id (or null) from localStorage. Safe to call
 * during render — does not subscribe to changes. Use `null` as the
 * "no segment" sentinel.
 */
export function getCapturedSegmentId() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id ?? null;
  } catch {
    return null;
  }
}
